import React, { useState, useEffect } from "react";
import {
  Box, Card, CardContent, Typography, Grid, Chip, Alert, LinearProgress,
  IconButton, Tooltip, Button, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Select, MenuItem, FormControl, InputLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Collapse
} from "@mui/material";
import AddIcon       from "@mui/icons-material/Add";
import EditIcon      from "@mui/icons-material/Edit";
import DeleteIcon    from "@mui/icons-material/Delete";
import StoreIcon     from "@mui/icons-material/Store";
import RouterIcon    from "@mui/icons-material/Router";
import SettingsIcon  from "@mui/icons-material/Settings";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MapIcon        from "@mui/icons-material/Map";
import { useNavigate } from "react-router-dom";
import { useAuth }     from "../contexts/AuthContext";
import api, { floorplanAPI } from "../services/api";

export default function Sites() {
  const { user }               = useAuth();
  const navigate               = useNavigate();
  const isAdmin                = user?.role === "ADMIN_REDIA";

  const [sites,       setSites]       = useState([]);
  const [clients,     setClients]     = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [success,     setSuccess]     = useState("");
  const [expanded,    setExpanded]    = useState({});

  // Dialogs
  const [createDialog, setCreateDialog] = useState(false);
  const [configDialog, setConfigDialog] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [selectedSite, setSelectedSite] = useState(null);

  const [floorplanDialog, setFloorplanDialog] = useState(false);
  const [floorplanUrl,    setFloorplanUrl]    = useState(null);
  const [floorplanSite,   setFloorplanSite]   = useState(null);

  const [createForm, setCreateForm] = useState({
    site_id: "", name: "", address: "", client_id: ""
  });
  const [configForm, setConfigForm] = useState({
    rssi_min: -75, dwell_min_sec: 180, dwell_max_sec: 10800,
    seen_count_min: 2, opening_time: "09:00", closing_time: "22:00",
    session_timeout_sec: 180, wifi_coverage_pct: 87, employee_min_hours: 4.0
  });

  useEffect(() => {
    loadSites();
    loadClients();
  }, []);

  const loadSites = () => {
    setLoading(true);
    api.get("/v1/sites")
      .then(r => setSites(r.data.sites || []))
      .catch(() => setError("Impossible de charger les sites"))
      .finally(() => setLoading(false));
  };

  const loadClients = () => {
    api.get("/v1/clients")
      .then(r => setClients(r.data.clients || []))
      .catch(() => {});
  };

  const openConfig = (site) => {
    setSelectedSite(site);
    api.get("/v1/sites/" + site.site_id)
      .then(r => {
        const cfg = r.data.config || {};
        setConfigForm({
          rssi_min:           cfg.rssi_min           ?? -75,
          dwell_min_sec:      cfg.dwell_min_sec      ?? 180,
          dwell_max_sec:      cfg.dwell_max_sec      ?? 10800,
          seen_count_min:     cfg.seen_count_min     ?? 2,
          opening_time:       cfg.opening_time       ?? "09:00",
          closing_time:       cfg.closing_time       ?? "22:00",
          session_timeout_sec:cfg.session_timeout_sec?? 180,
          wifi_coverage_pct:  cfg.wifi_coverage_pct  ?? 87,
          employee_min_hours: cfg.employee_min_hours ?? 4.0,
        });
        setConfigDialog(true);
      })
      .catch(() => setError("Impossible de charger la config"));
  };

  const createSite = async () => {
    try {
      const payload = {
        site_id: createForm.site_id,
        name:    createForm.name,
        address: createForm.address || null
      };
      if (createForm.client_id) {
        await api.post("/v1/clients/" + createForm.client_id + "/sites", payload);
      } else {
        await api.post("/v1/sites", payload);
      }
      setSuccess("Site \"" + createForm.name + "\" cree");
      setCreateDialog(false);
      setCreateForm({ site_id: "", name: "", address: "", client_id: "" });
      loadSites();
    } catch(e) {
      setError(e.response?.data?.detail || "Erreur creation site");
    }
  };

  const saveConfig = async () => {
    if (!selectedSite) return;
    try {
      await api.put("/v1/sites/" + selectedSite.site_id + "/config", configForm);
      setSuccess("Config site \"" + selectedSite.site_id + "\" sauvegardee");
      setConfigDialog(false);
      loadSites();
    } catch(e) {
      setError(e.response?.data?.detail || "Erreur sauvegarde config");
    }
  };

  const deleteSite = async () => {
    if (!selectedSite) return;
    try {
      await api.delete("/v1/sites/" + selectedSite.site_id);
      setSuccess("Site \"" + selectedSite.site_id + "\" supprime");
      setDeleteDialog(false);
      setSelectedSite(null);
      loadSites();
    } catch(e) {
      setError(e.response?.data?.detail || "Erreur suppression site");
    }
  };

  const openFloorplan = (site) => {
    setFloorplanSite(site);
    setFloorplanUrl(floorplanAPI.getUrl(site.site_id) + "?t=" + Date.now());
    setFloorplanDialog(true);
  };

  const handleFloorplanUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !floorplanSite) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await floorplanAPI.upload(floorplanSite.site_id, fd);
      setFloorplanUrl(floorplanAPI.getUrl(floorplanSite.site_id) + "?t=" + Date.now());
      setSuccess("Plan uploade pour " + floorplanSite.site_id);
    } catch(e) {
      setError("Erreur upload plan");
    }
  };

  const handleFloorplanDelete = async () => {
    if (!floorplanSite) return;
    try {
      await floorplanAPI.remove(floorplanSite.site_id);
      setFloorplanUrl(null);
      setSuccess("Plan supprime");
    } catch(e) {
      setError("Erreur suppression plan");
    }
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between",
        alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1F4E79">
            Sites
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {sites.length} site(s) configure(s)
          </Typography>
        </Box>
        {isAdmin && (
          <Button variant="contained" startIcon={<AddIcon />}
            onClick={() => setCreateDialog(true)}
            sx={{ bgcolor: "#1F4E79", "&:hover": { bgcolor: "#163a5f" } }}>
            Nouveau site
          </Button>
        )}
      </Box>

      {error   && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setError("")}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      <TableContainer component={Paper} sx={{ borderRadius: 3,
        boxShadow: "0 2px 12px rgba(0,0,0,0.08)" }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#F8FBFF" }}>
              <TableCell sx={{ fontWeight: 700, color: "#1F4E79" }}>Site ID</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1F4E79" }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1F4E79" }}>Client</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1F4E79" }}>Capteurs</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1F4E79" }}>Config</TableCell>
              <TableCell sx={{ fontWeight: 700, color: "#1F4E79" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.map(site => (
              <TableRow key={site.site_id}
                sx={{ "&:hover": { bgcolor: "#F5F7FA" } }}>
                <TableCell>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <StoreIcon sx={{ color: "#4CAF50", fontSize: 18 }} />
                    <Typography fontWeight={600} fontSize="0.9rem">
                      {site.site_id}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography fontSize="0.9rem">{site.name}</Typography>
                  {site.address && (
                    <Typography variant="caption" color="text.secondary">
                      {site.address}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography fontSize="0.85rem" color="text.secondary">
                    {site.client_name || "?"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip icon={<RouterIcon sx={{ fontSize: "14px !important" }} />}
                    label={site.sensor_count ?? 0}
                    size="small"
                    sx={{ bgcolor: "#E8F5E9", color: "#4CAF50" }} />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                    {site.rssi_min != null && (
                      <Chip label={"RSSI " + site.rssi_min} size="small"
                        sx={{ bgcolor: "#EEF4FF", color: "#1F4E79", fontSize: "0.7rem" }} />
                    )}
                    {site.dwell_min_sec != null && (
                      <Chip label={"Dwell " + site.dwell_min_sec + "s"} size="small"
                        sx={{ bgcolor: "#FFF8E1", color: "#F57F17", fontSize: "0.7rem" }} />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    <Tooltip title="Tableau de bord">
                      <IconButton size="small" color="primary"
                        onClick={() => navigate("/dashboard",
                          { state: { siteId: site.site_id } })}>
                        <DashboardIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Plan du site">
                      <IconButton size="small" sx={{ color: "#2E7D32" }}
                        onClick={() => openFloorplan(site)}>
                        <MapIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {isAdmin && (
                      <Tooltip title="Configuration">
                        <IconButton size="small" color="primary"
                          onClick={() => openConfig(site)}>
                          <SettingsIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {isAdmin && (
                      <Tooltip title="Supprimer">
                        <IconButton size="small" color="error"
                          onClick={() => { setSelectedSite(site); setDeleteDialog(true); }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
            {sites.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  Aucun site configure
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog creation site */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Nouveau site</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Client (optionnel)</InputLabel>
                <Select value={createForm.client_id} label="Client (optionnel)"
                  onChange={e => setCreateForm(p => ({...p, client_id: e.target.value}))}>
                  <MenuItem value="">Aucun client</MenuItem>
                  {clients.map(c => (
                    <MenuItem key={c.client_id} value={c.client_id}>{c.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {[
              ["site_id", "Site ID (ex: JAZZ-2026)", true],
              ["name",    "Nom du site",              true],
              ["address", "Adresse",                  false],
            ].map(([key, label, required]) => (
              <Grid item xs={12} key={key}>
                <TextField fullWidth label={label} required={required}
                  value={createForm[key]}
                  onChange={e => setCreateForm(p => ({...p, [key]: e.target.value}))} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={createSite}
            disabled={!createForm.site_id || !createForm.name}
            sx={{ bgcolor: "#1F4E79" }}>
            Creer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog configuration site */}
      <Dialog open={configDialog} onClose={() => setConfigDialog(false)}
        maxWidth="sm" fullWidth>
        <DialogTitle>Configuration — {selectedSite?.site_id}</DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            {[
              ["rssi_min",            "RSSI minimum (dBm)",    "number"],
              ["dwell_min_sec",       "Dwell minimum (sec)",   "number"],
              ["dwell_max_sec",       "Dwell maximum (sec)",   "number"],
              ["seen_count_min",      "Seen count minimum",    "number"],
              ["session_timeout_sec", "Timeout session (sec)", "number"],
              ["wifi_coverage_pct",   "Couverture WiFi (%)",   "number"],
              ["employee_min_hours",  "Seuil employe (h)",     "number"],
              ["opening_time",        "Heure ouverture",       "text"],
              ["closing_time",        "Heure fermeture",       "text"],
            ].map(([key, label, type]) => (
              <Grid item xs={6} key={key}>
                <TextField fullWidth label={label} type={type} size="small"
                  InputLabelProps={{ shrink: true }}
                  value={configForm[key] ?? ""}
                  onChange={e => setConfigForm(p => ({...p, [key]: type === "number" ? Number(e.target.value) : e.target.value}))} />
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfigDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={saveConfig}
            sx={{ bgcolor: "#1F4E79" }}>
            Sauvegarder
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog suppression site */}
      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}
        maxWidth="xs" fullWidth>
        <DialogTitle sx={{ color: "#d32f2f" }}>Supprimer le site</DialogTitle>
        <DialogContent>
          <Typography>
            Supprimer <strong>{selectedSite?.site_id}</strong> ?
            Cette action supprimera le site et sa configuration.
            Les donnees historiques (visits, probes) seront preservees.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteDialog(false)}>Annuler</Button>
          <Button variant="contained" color="error" onClick={deleteSite}>
            Supprimer
          </Button>
        </DialogActions>
      </Dialog>
      {/* Dialog plan du site */}
      <Dialog open={floorplanDialog} onClose={() => setFloorplanDialog(false)}
        maxWidth="md" fullWidth>
        <DialogTitle>Plan du site — {floorplanSite?.site_id}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Box sx={{ textAlign: "center", mb: 2 }}>
            <img
              src={floorplanUrl}
              alt="Plan du site"
              onError={(e) => { e.target.style.display = "none"; }}
              style={{ maxWidth: "100%", maxHeight: 420, borderRadius: 8,
                border: "1px solid #ddd" }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
            <Button variant="contained" component="label"
              sx={{ bgcolor: "#1F4E79" }}>
              {floorplanUrl ? "Remplacer le plan" : "Uploader un plan"}
              <input type="file" hidden accept="image/*"
                onChange={handleFloorplanUpload} />
            </Button>
            {floorplanUrl && (
              <Button variant="outlined" color="error"
                onClick={handleFloorplanDelete}>
                Supprimer
              </Button>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setFloorplanDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
